import { GLOBAL_CONSTANTS } from 'assets/js/utils/constants'
import { loadBaseMover } from 'tsparticles-move-base'
import { loadColorUpdater } from 'tsparticles-updater-color'
import { loadEmittersPlugin } from 'tsparticles-plugin-emitters'
import { loadExternalRepulseInteraction } from 'tsparticles-interaction-external-repulse'
import { loadLifeUpdater } from 'tsparticles-updater-life'
import { loadOutModesUpdater } from 'tsparticles-updater-out-modes'
import { loadRollUpdater } from 'tsparticles-updater-roll'
import { loadRotateUpdater } from 'tsparticles-updater-rotate'
import { loadSizeUpdater } from 'tsparticles-updater-size'
import { loadSquareShape } from 'tsparticles-shape-square'
import { loadWobbleUpdater } from 'tsparticles-updater-wobble'
import { tsParticles } from 'tsparticles-engine'
import 'wicg-inert'

const SELECTORS = {
  modal: '.js-kc7-quiz-modal',
  modalClose: '.js-kc7-quiz-modal-close',
  item: '.js-kc7-quiz-item',
  skipLink: '.js-kc7-quiz-skiplink',
  modalHeadline: '.js-kc7-quiz-modal-headline',
  modalHeadlineText: '.js-kc7-quiz-modal-headline-text',
  modalHeadlineImage: '.js-kc7-quiz-modal-headline-image',
  modalEyebrow: '.js-kc7-quiz-modal-eyebrow',
  modalBody: '.js-kc7-quiz-modal-body',
  modalDesc: '.js-kc7-quiz-modal-desc',
  modalConfettiDesc: '.js-kc7-quiz-modal-confetti-desc',
  altGameButton: '.js-kc7-quiz-alt-game-button'
}

class KC7Quiz {
  constructor(element) {
    this.element = element
    this.modal = this.element.querySelector(SELECTORS.modal)
    this.modalClose = this.element.querySelector(SELECTORS.modalClose)
    this.items = this.element.querySelectorAll(SELECTORS.item)
    this.skipLink = this.element.querySelector(SELECTORS.skipLink)
    this.modalHeadline = this.element.querySelector(SELECTORS.modalHeadline)
    this.modalHeadlineText = this.element.querySelector(SELECTORS.modalHeadlineText)
    this.modalHeadlineImage = this.element.querySelector(SELECTORS.modalHeadlineImage)
    this.modalEyebrow = this.element.querySelector(SELECTORS.modalEyebrow)
    this.modalBody = this.element.querySelector(SELECTORS.modalBody)
    this.modalDesc = this.element.querySelector(SELECTORS.modalDesc)
    this.modalConfettiDesc = this.element.querySelector(SELECTORS.modalConfettiDesc)
    this.altGameButton = this.element.querySelector(SELECTORS.altGameButton)
    this.page = document.querySelector('.page-container')
    this.video = window.unlocked?.videos?.[this.element.id]
    this.emitter = GLOBAL_CONSTANTS.UTILS.EMITTER
    this.videoState = null

    this.defaultHeadline = this.modal.dataset.defaultHeadline
    this.defaultBody = this.modal.dataset.defaultBody

    this.confetti = undefined
    this.confettiTimeout = undefined
    this.confettiColors = this.modal.dataset.confettiColors.split(',')
    this.confettiSize = Number(this.modal.dataset.confettiSize)
    this.confettiPerFire = Number(this.modal.dataset.confettiPerFire)
    this.confettiDuration = Number(this.modal.dataset.confettiDuration)
    this.confettiGravityMin = Number(this.modal.dataset.confettiGravityMin)
    this.confettiGravityMax = Number(this.modal.dataset.confettiGravityMax)
    this.confettiIsInteractive = this.modal.dataset.confettiIsInteractive === 'true'
    this.confettiEnable3D = this.modal.dataset['confettiEnable-3d'] === 'true'
    this.confettiEnableMouseExplosions = this.modal.dataset.confettiEnableMouseExplosions === 'true'
    this.confettiCountPerExplosion = Number(this.modal.dataset.confettiCountPerExplosion)
    this.confettiExplosionDirection = this.modal.dataset.confettiExplosionDirection

    this.init()
  }

  /**
   * Initialize the component
   */
  init() {
    this.registerEventListeners()
    this.getSkipLink(this.element)
    this.setItemWidth()
    this.page.parentElement.appendChild(this.modal)
  }

  /**
   * Registers all event listeners
   */
  registerEventListeners = () => {
    const stylesheet = document.querySelector('#compiled-styles-css')

    // Open modal
    this.items.forEach(item => {
      item.addEventListener(GLOBAL_CONSTANTS.EVENTS.CLICK, this.openModal, true)
    })

    // Toggle text alt game
    this.altGameButton?.addEventListener(GLOBAL_CONSTANTS.EVENTS.CLICK, this.toggleAltGame, true)

    // Close modal
    this.modalClose.addEventListener(GLOBAL_CONSTANTS.EVENTS.CLICK, this.closeModal)
    document.addEventListener(GLOBAL_CONSTANTS.EVENTS.KEYDOWN, this.escKeyHandler)

    // Set item width CSS var
    this.emitter.on(GLOBAL_CONSTANTS.EVENTS.RESIZE, () => this.setItemWidth())
    stylesheet.addEventListener('load', () => this.setItemWidth())
  }

  /**
   * Async action that triggers the confetti effect
   * @returns {Promise}
   */
  fireConfetti = async () => {
    await loadBaseMover(tsParticles)
    await loadColorUpdater(tsParticles)
    await loadEmittersPlugin(tsParticles)
    await loadExternalRepulseInteraction(tsParticles)
    await loadLifeUpdater(tsParticles)
    await loadOutModesUpdater(tsParticles)
    await loadRollUpdater(tsParticles)
    await loadRotateUpdater(tsParticles)
    await loadSizeUpdater(tsParticles)
    await loadSquareShape(tsParticles)
    await loadWobbleUpdater(tsParticles)

    const options = {
      autoPlay: true,
      detectRetina: true,
      fpsLimit: 60,
      pauseOnBlur: true,
      particles: {
        number: {
          density: {
            enable: true,
            width: 300,
            height: 300
          },
          value: 26
        },
        color: {
          value: this.confettiColors
        },
        shape: {
          type: 'square'
        },
        size: {
          value: this.confettiSize
        },
        rotate: {
          random: true,
          direction: 'random',
          value: {
            min: 0,
            max: 360
          },
          animation: {
            enable: false,
            speed: 30
          }
        },
        roll: {
          enable: false,
          mode: 'vertical',
          speed: {
            min: 15,
            max: 25
          }
        },
        wobble: {
          distance: 30,
          enable: false,
          speed: {
            min: -7,
            max: 7
          }
        }
      }
    }

    if (!document.documentElement.classList.contains('reduced-motion')) {
      const emitterBase = {
        position: {},
        rate: {
          quantity: this.confettiPerFire,
          delay: 0.2
        },
        particles: {
          move: {
            outModes: {
              top: 'none',
              left: 'none',
              right: 'none',
              default: 'destroy'
            }
          }
        },
        life: {
          count: 1,
          duration: this.confettiDuration,
          delay: 0
        }
      }

      const emitterLeft1 = structuredClone(emitterBase)
      const emitterLeft2 = structuredClone(emitterBase)
      const emitterRight1 = structuredClone(emitterBase)
      const emitterRight2 = structuredClone(emitterBase)

      emitterLeft1.position.x = -5
      emitterLeft2.position.x = -5
      emitterRight1.position.x = 105
      emitterRight2.position.x = 105

      emitterLeft1.position.y = 50
      emitterLeft2.position.y = 90
      emitterRight1.position.y = 50
      emitterRight2.position.y = 90

      emitterLeft1.particles.move.direction = 'top-right'
      emitterLeft2.particles.move.direction = 'top-right'
      emitterRight1.particles.move.direction = 'top-left'
      emitterRight2.particles.move.direction = 'top-left'

      options.emitters = [
        { ...emitterLeft1 },
        { ...emitterLeft2 },
        { ...emitterRight1 },
        { ...emitterRight2 }
      ]

      options.particles.number = {
        value: 0
      }

      options.particles.move = {
        decay: 0.05,
        direction: 'top',
        enable: true,
        gravity: {
          enable: true,
          acceleration: {
            min: Math.min(this.confettiGravityMin, this.confettiGravityMax),
            max: Math.max(this.confettiGravityMin, this.confettiGravityMax)
          }
        },
        outModes: {
          top: 'none',
          default: 'destroy'
        },
        speed: {
          min: 50,
          max: 100
        }
      }

      options.particles.wobble.enable = true

      options.particles.roll.enable = this.confettiEnable3D

      options.particles.rotate.animation.enable = true

      options.interactivity = {
        detectsOn: 'window',
        events: {
          onHover: {
            enable: this.confettiIsInteractive,
            mode: 'repulse'
          },
          onClick: {
            enable: this.confettiEnableMouseExplosions,
            mode: 'emitter'
          }
        },
        modes: {
          emitters: {
            direction: this.confettiExplosionDirection,
            life: {
              count: 1,
              duration: 0.1,
              delay: 0
            },
            rate: {
              quantity: this.confettiCountPerExplosion,
              delay: 0.1
            },
            size: this.confettiSize
          }
        }
      }

      this.modalConfettiDesc.innerHTML = 'Colorful square confetti shoots from either side of the screen in celebration'
    }

    await tsParticles
      .load(this.modal.id, options)
      .then(container => {
        this.confetti = container
      })
      .catch(error => {
        console.error(error)
      })
  }

  /**
   * Sets the HREF of the game's skip link by finding the next item with an ID
   */
  getSkipLink = (element) => {
    const next = element.nextElementSibling
    if (next !== null) {
      if (next.hasAttribute('id')) {
        this.skipLink.href = `#${next.id}`
      } else {
        this.getSkipLink(next)
      }
    } else {
      const parent = element.parentElement
      if (element.parentElement !== null) {
        this.getSkipLink(parent)
      } else {
        this.skipLink.remove()
      }
    }
  }

  /**
   * Sets --kc7-item-width variable that helps set the button layout grid
   */
  setItemWidth = () => {
    const widths = Array.from(this.items).map(item => item.offsetWidth)
    const maxWidth = Math.max(...widths)

    this.element.style.setProperty('--kc7-item-width', `${maxWidth}px`)
  }

  /**
   * Opens the modal associated with the clicked button
   * @param {PointerEvent} evt - The click event that triggered the action
   */
  openModal = (evt) => {
    const { isCorrect, term, definition, titleImage, index } = evt.currentTarget.dataset

    if (isCorrect === 'true') {
      this.modal.dataset.correct = 'true'
      this.modalHeadlineText.innerHTML = term
      this.modalBody.innerHTML = definition
      this.modalDesc.innerHTML = `${this.modalEyebrow.innerHTML}. ${term}: ${definition}`

      if (titleImage !== undefined) {
        this.modalHeadlineImage.innerHTML = titleImage
        this.modalHeadline.classList.add('kc7-quiz__modal__headline--show-image')
      }

      this.fireConfetti()
    } else {
      this.modal.dataset.correct = 'false'
      this.modalHeadlineText.innerHTML = this.defaultHeadline
      this.modalHeadlineImage.innerHTML = ''
      this.modalBody.innerHTML = this.defaultBody
      this.modalDesc.innerHTML = `${this.defaultHeadline}. ${this.defaultBody}`
      this.modalHeadline.classList.remove('kc7-quiz__modal__headline--show-image')
    }

    this.modal.dataset.buttonIndex = index
    this.modal.classList.add('kc7-quiz__modal--active')
    this.videoState = this.video.state()
    this.video.pause()
    this.video.lock()

    // Manage focus and inert state
    this.page.inert = true
    this.modal.inert = false
    this.modalClose.focus()
  }

  /**
   * Closes the modal and resets confetti and video states
   */
  closeModal = () => {
    const button = this.element.querySelector(`${SELECTORS.item}[data-index="${this.modal.dataset.buttonIndex}"]`)

    delete this.modal.dataset.buttonIndex
    this.modal.classList.remove('kc7-quiz__modal--active')
    this.video.unlock()
    if (this.videoState !== 'pause') {
      this.video.play()
    }

    this.modalConfettiDesc.innerHTML = ''

    // Manage focus and inert state
    this.page.inert = false
    this.modal.inert = true
    button.focus()

    clearTimeout(this.confettiTimeout)
    this.confetti?.canvas.destroy()
  }

  /**
   * Triggers `closeModal` when the ESC key is pressed
   * @param {KeyboardEvent} evt - The keyboard event that triggers the action
   */
  escKeyHandler = (evt) => {
    if (evt.key === 'Escape' && !this.modal.inert) {
      evt.preventDefault()
      this.closeModal()
    }
  }

  /**
   * Toggles visibility of the textual alt for the video
   */
  toggleAltGame = () => {
    if (this.altGameButton.ariaPressed === 'true') {
      this.altGameButton.ariaPressed = 'false'
    } else {
      this.altGameButton.ariaPressed = 'true'
    }
  }
}

export default {
  selector: '.js-kc7-quiz',
  Source: KC7Quiz
}
